import { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from "@mui/material/Typography";
import CloseIcon from '@mui/icons-material/Close';
import Button from "@mui/material/Button";
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import 'semantic-ui-css/semantic.min.css'

import classes from 'app/components/Modal/classes.module.scss';

import {
  DISCOUNT_TYPE_PERCENT,
  DISCOUNT_TYPE_AMOUNT
} from 'app/utils/Constants';

import {
  getDiscountCode as getDiscountsSvc,
} from 'app/services/jobConfigs';

import {
  formatCurrency,
  objectsAreSame
} from 'app/utils/valueFormatters';

import {
  createDiscountCode as createDiscountCodeSvc,
  updateDiscountCode as updateDiscountCodeSvc,
  deleteDiscountCode as deleteDiscountCodeSvc
} from 'app/services/jobConfigs';

export const numberEditor = (options) => {
  return options.field === 'usage_count' ?
    <div>
      <InputNumber value={options.value} onChange={(e) => options.editorCallback(e.value)} />
    </div> :
    <div>
      <InputNumber value={options.value} onChange={(e) => options.editorCallback(e.value)} minFractionDigits={2} maxFractionDigits={5} />
    </div>
}

const DiscountForm = (props) => {
  const { isOpen, onClose, selectedJobConfig } = props;
  const [itemsChanged, setItemsChanged] = useState(false);
  const [data, setData] = useState([]);
  const [origData, setOrigData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const modalStyle = {};

  modalStyle.justifyContent = 'end';
  modalStyle.marginRight = '1.5rem';

  let boxClass = classes.paper,
    boxStyle = { margin: '1em' },
    headerVariant = 'h5';

  const discountTypes = [
    { key: 1, text: '% Percent', value: DISCOUNT_TYPE_PERCENT },
    { key: 2, text: '$ Amount', value: DISCOUNT_TYPE_AMOUNT }
  ]

  useEffect(() => {
    if (!loaded) {
      getDiscountsSvc(selectedJobConfig?.product_id).then(results => {
        setData(results);
        setOrigData(results);
        setLoaded(true);
      })
    }
  }, [loaded, isOpen])


  useEffect(() => {
    let isChanged = false;

    if (!objectsAreSame(origData, data)) {
      isChanged = true;
    }

    setItemsChanged(isChanged);
  }, [origData, data, isOpen])

  const onSubmit = async () => {
    for (var discount of data) {


      discount.discount_value = discount.discount_value * 100;
      discount.minimum_total_amount = discount.minimum_total_amount * 100;

      if (discount.id > 0) {
        await updateDiscountCodeSvc(selectedJobConfig?.product_id, discount.id, discount)
      } else {
        await createDiscountCodeSvc(selectedJobConfig?.product_id, discount);
      }
    }

    let forDeletes = [];
    let dataIds = data.map(d => { return d.id });
    for (let orig of origData) {
      if (orig.id && !dataIds.includes(orig.id)) {
        forDeletes.push(orig);
      }
    }

    for (let item of forDeletes) {
      await deleteDiscountCodeSvc(selectedJobConfig?.product_id, item.id);
    }

    setLoaded(false);
    setItemsChanged(false);
    onClose();
  }

  const handleDeleteRow = (rowIndex) => {
    const discounts = [...data];
    discounts.splice(rowIndex, 1);
    setData(discounts);
  }

  const handleClose = () => {
    setLoaded(false);
    setItemsChanged(false);
    onClose();
  }

  const handleAddRow = () => {
    const discounts = [...data];
    discounts.push({ id: new Date().getTime() * -1, unique_code: '', discount_value: 0, minimum_total_amount: 0, discount_type: DISCOUNT_TYPE_PERCENT });
    setData(discounts);
  }

  const valueBodyTemplate = (rowData, column) => {
    if (rowData.discount_type === DISCOUNT_TYPE_AMOUNT) {
      return <label>{formatCurrency(rowData[column.field], selectedJobConfig?.currency)}</label>
    } else if (rowData.discount_type === DISCOUNT_TYPE_PERCENT) {
      return <label>{(rowData[column.field]) + '%'}</label>
    }
  }

  const discountTypeTemplate = (rowData, column) => {
    if (rowData.discount_type === DISCOUNT_TYPE_AMOUNT) {
      return <label>$ Amount</label>
    } else if (rowData.discount_type === DISCOUNT_TYPE_PERCENT) {
      return <label>% Percent</label>
    }
  }

  const currencyBodyTemplate = (rowData, column) => {
    return <label>{formatCurrency(rowData[column.field], selectedJobConfig?.currency)}</label>
  }

  const onRowEditComplete = (e) => {
    let discounts = [...data];
    let { newData, index } = e;

    discounts[index] = newData;
    discounts.forEach((item) => {
      item.product_id = selectedJobConfig?.product_id;
    })

    setData(discounts);
  };

  const textEditor = (options) => {
    return <div>
      <InputText value={options.value} onChange={(e) => options.editorCallback(e.target.value)} />
    </div>
  }

  const discountTypeEditor = (options) => {
    return (
      <Select
        value={options.value}
        onChange={(e) => options.editorCallback(e.target.value)}>
        {
          discountTypes.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.text}</MenuItem>
          ))
        }
      </Select>
    )
  }

  const tableFooter = () => {
    return (
      <div>
        <Button variant="contained" onClick={handleAddRow}>Add</Button>
      </div>
    )
  }

  const deleteRowBody = (data, row) => {
    return <div>
      <IconButton aria-label="delete" onClick={() => handleDeleteRow(row.rowIndex)}>
        <DeleteIcon />
      </IconButton>
    </div>
  }

  return <Modal open={isOpen} className={classes.modal} onClose={onClose}>
    <Box className={boxClass} sx={boxStyle}>
      <CloseIcon className={classes.closeIcon} onClick={onClose} />
      <Typography variant={headerVariant} className={classes.formTitle}>
        Discount Codes
      </Typography>

      <DataTable value={data} dataKey="id" editMode="row" footer={tableFooter} onRowEditComplete={onRowEditComplete}>
        <Column field="unique_code" header="Discount Code" editor={(options) => textEditor(options)} ></Column>
        <Column field="discount_value" header="Discount Value" body={valueBodyTemplate} editor={(options) => numberEditor(options)}></Column>
        <Column field="minimum_total_amount" header="Minimum Total Amount" body={currencyBodyTemplate} editor={(options) => numberEditor(options)}></Column>
        <Column field="discount_type" header="Type" body={discountTypeTemplate} editor={(options) => discountTypeEditor(options)} style={{ width: '20%' }}></Column>
        <Column field="usage_count" header="Usage Count" editor={(options) => numberEditor(options)}></Column>
        <Column rowEditor={true} headerStyle={{ width: '10%', minWidth: '8rem' }} bodyStyle={{ textAlign: 'center' }}></Column>
        <Column body={deleteRowBody} ></Column>
      </DataTable>

      <Button className={classes.button} variant="contained" onClick={handleClose}>Cancel</Button>
      <Button className={classes.button} disabled={!itemsChanged} variant="contained" onClick={onSubmit}>Save</Button>
    </Box>
  </Modal>
}

export default DiscountForm;
